import React, { useEffect, useRef } from 'react';
import { Animated, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { useTheme } from '../../../theme';

interface SkeletonBlockProps {
  /** Layout for the placeholder (width / height / margins / radius overrides). */
  style?: StyleProp<ViewStyle>;
}

/**
 * A single shimmering grey block used to build loading skeletons.
 *
 * Each analytics component composes its *own* skeleton layout from these blocks
 * so the placeholder always matches that component's final shape. This file only
 * owns the shared pulse animation so the timing stays identical everywhere and
 * the animation loop isn't duplicated in every component.
 */
const SkeletonBlock: React.FC<SkeletonBlockProps> = ({ style }) => {
  const shimmer = useRef(new Animated.Value(0)).current;
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmer, { toValue: 1, duration: 900, useNativeDriver: true }),
        Animated.timing(shimmer, { toValue: 0, duration: 900, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [shimmer]);

  const opacity = shimmer.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0.75] });

  return (
    <Animated.View
      style={[styles.block, isDark && { backgroundColor: '#404040' }, style, { opacity }]}
    />
  );
};

const styles = StyleSheet.create({
  block: {
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
  },
});

export default SkeletonBlock;
